// ANA-R3-GEOM-04 — edit-existing-geometry state.
//
// When a saved defect is selected in the Viewer overlay and editing is
// allowed, a small floating toolbar offers "Edit geometry". Activating it
// mounts the shared `PolygonEditor` (GEOM-02) over the OSD viewport transform
// (the SAME seam GEOM-03's DrawOverlay uses), seeded from the defect's GEOM-01
// `path` (normalised) — or, for a legacy row with no path, from its 4 bbox
// corners. The analyst moves / adds / removes vertices (or constrained-resizes
// a rect); the edits are a DRAFT layered over the persisted shape (the overlay
// shows the draft, the host hides the original). An explicit Save commits via
// `PATCH /defects/:id/geometry`; Cancel / Esc discards the draft and restores
// the persisted shape — nothing persists without Save (AC2).
//
// The store holds only UI/draft state (Zustand, ephemeral). It owns NO geometry
// undo stack — the PolygonEditor primitive owns that, and the host's per-image
// undo stack routes Ctrl+Z to `api.undo()` (GEOM-04 step 2: no parallel stack).
import { create } from "zustand";
import {
  type EditorMode,
  type Point,
  detectMode,
  parsePath,
  rectCorners,
} from "@aeriosiq/ui";

export interface EditGeometryState {
  /** The defect currently being edited, or null when not editing. */
  defectId: string | null;
  /** The image the edited defect lives on (drives the per-image undo bucket). */
  imageId: string | null;
  /** Editor mode (rect-resize / polygon / polyline). */
  mode: EditorMode;
  /** Draft vertices, NORMALISED 0..1, no closing duplicate (editor shape). */
  points: Point[];

  /**
   * Begin editing `defectId`. `path` is the defect's NORMALISED read path (flat
   * `[x0,y0,…]`); when null/empty, `bbox` (normalised, `{x,y,w,h}`) seeds the 4
   * corners as a rect (legacy rows — AC4). The initial mode is chosen by
   * `detectMode` (axis-aligned closed quad ⇒ rect; other closed ⇒ polygon; open
   * ⇒ polyline).
   */
  begin: (args: {
    defectId: string;
    imageId: string;
    path: number[] | null | undefined;
    bbox: { x: number; y: number; w: number; h: number } | null | undefined;
  }) => void;
  /** Replace the draft vertices + mode (driven by the PolygonEditor onChange). */
  setDraft: (mode: EditorMode, points: Point[]) => void;
  /** Cancel/discard the draft and leave edit mode (AC2). */
  cancel: () => void;
  /** Clear after a successful Save (host has refetched the server shape). */
  finish: () => void;
}

/**
 * Seed the editor's `{ mode, points }` from a defect's normalised geometry.
 * Prefers the explicit `path`; falls back to the 4 bbox corners (legacy, AC4).
 * Returns a rect with no vertices only when neither is usable (degenerate).
 */
export function seedFromGeometry(
  path: number[] | null | undefined,
  bbox: { x: number; y: number; w: number; h: number } | null | undefined,
): { mode: EditorMode; points: Point[] } {
  if (path && path.length >= 6) {
    const mode = detectMode(path);
    const { points } = parsePath(path);
    return { mode, points };
  }
  if (bbox && bbox.w > 0 && bbox.h > 0) {
    // Legacy: seed the 4 bbox corners as a constrained-resize rect (AC4).
    return {
      mode: "rect",
      points: rectCorners({
        x: bbox.x,
        y: bbox.y,
        width: bbox.w,
        height: bbox.h,
      }),
    };
  }
  return { mode: "rect", points: [] };
}

export const useEditGeometryStore = create<EditGeometryState>((set) => ({
  defectId: null,
  imageId: null,
  mode: "rect",
  points: [],

  begin: ({ defectId, imageId, path, bbox }) => {
    const seed = seedFromGeometry(path, bbox);
    set({ defectId, imageId, mode: seed.mode, points: seed.points });
  },
  setDraft: (mode, points) => {
    set({ mode, points });
  },
  cancel: () => {
    set({ defectId: null, imageId: null, mode: "rect", points: [] });
  },
  finish: () => {
    set({ defectId: null, imageId: null, mode: "rect", points: [] });
  },
}));
